import {
  FileUploadConfig,
  isFileUploadValidationError,
  ProjectDecisionAttachment,
  UploadedAttachment,
  validateFiles,
} from "../fileUpload/fileUploadDomain";
import {
  AttachmentType,
  DbotEventDispatcher,
  DeleteAttachmentEvent,
  deleteAttachmentSuccessEvent,
  DocumentContext,
  ProjectContext,
  sortDocuments,
  supportsAttachments,
  UploadAttachmentsEvent,
  uploadAttachmentsFailure,
  uploadAttachmentsSuccess,
} from "./common/commonDomain";
import { logError } from "../utils/log";
import { AxiosInstance } from "axios";
import { createFileTypeValidationErrorMessage } from "../fileUpload/fileUploadErrorMessages";
import { getFilenameFromHeaders } from "../api";
import { DecisionAndForm, DocumentAndForm, DocumentData } from "./project/projectDomain";
import {
  AttachmentPanelProps,
  DecisionAttachmentPanelProps,
} from "../components-v2/AttachmentsPanel";
import { SelectFilesButtonProps } from "../components-v2/SelectFilesButton";
import { action, runInAction } from "mobx";
import { remove } from "lodash";
import { DocumentType } from "../common/documents";
import { DecisionType } from "../common/decisions";
import { showError, showGeneralError } from "../events/eventsDomain";

export const uploadAttachments = (
  projectId: string,
  event: UploadAttachmentsEvent,
  fileUploadConfig: FileUploadConfig,
  dispatch: DbotEventDispatcher,
  http: AxiosInstance,
  documents: DocumentAndForm[],
  decisions: DecisionAndForm<DecisionType>[],
) => {
  if (!event.files.length) {
    return;
  }
  const document = documents.find(
    // TODO remove check on custom_document once all projects has been migrated: https://dcompanyteam.atlassian.net/browse/DCOM-4901
    (d) => d.documentId === event.reference && d.type === DocumentType.custom_document,
  );
  const decision = decisions.find((d) => d.decisionId === event.reference);

  if (document || decision) {
    dispatch({
      type: "dbot/change-state",
      state: {
        type: "uploading-attachments",
        reference: event?.reference,
      },
    });
  }
  validateFiles(event.files, fileUploadConfig)
    .then((files) => {
      const formData = new FormData();
      for (const file of files) {
        formData.append("files", file);
      }
      formData.set("reference", event.reference);
      formData.set("attachmentType", event.attachmentType);
      formData.set(
        "overrideExistingAttachmentType",
        `${event.overrideExistingAttachmentType ?? false}`,
      );
      return http.post<UploadedAttachment[]>(`/project/${projectId}/attachment`, formData);
    })
    .then(
      action((res) => {
        if (document) document.attachments.push(...res.data);
        else decision?.attachments.push(...res.data.map((a) => ({ ...a, selectedDocuments: [] })));

        dispatch(
          uploadAttachmentsSuccess(
            res.data,
            event.reference,
            event.source,
            event.selectedDocuments,
          ),
        );
      }),
    )
    .catch((err) => {
      if (isFileUploadValidationError(err)) {
        logError(err, "Invalid attachments", {
          reference: event.reference,
          attachmentType: event.attachmentType,
        });
        dispatch([showError(createFileTypeValidationErrorMessage(err))]);
      } else {
        dispatch([showGeneralError()]);
      }
      dispatch(uploadAttachmentsFailure(event.reference, event.source));
    })
    .finally(() => {
      if (document || decision) {
        dispatch({ type: "dbot/change-state", state: { type: "ready" } });
      }
    });
};

export const deleteAttachment = (
  projectId: string,
  event: DeleteAttachmentEvent,
  dispatch: DbotEventDispatcher,
  http: AxiosInstance,
  documents: DocumentAndForm[],
  decisions: DecisionAndForm<DecisionType>[],
) => {
  const document = documents.find(
    // TODO remove check on custom_document once all projects has been migrated: https://dcompanyteam.atlassian.net/browse/DCOM-4901
    (d) => d.documentId === event.reference && d.type === DocumentType.custom_document,
  );
  const decision = decisions.find((d) => d.decisionId === event.reference);

  if (document || decision) {
    dispatch({
      type: "dbot/change-state",
      state: {
        type: "deleting-attachment",
        attachmentId: event.attachmentId,
        reference: event?.reference,
      },
    });
  }
  http
    .delete(`/project/${projectId}/attachment/${event.attachmentId}`)
    .then(
      action(() => {
        if (document) {
          remove(document.attachments, (a) => a.id === event.attachmentId);
        }
        if (decision) {
          remove(decision.attachments, (a) => a.id === event.attachmentId);
        }
        dispatch(deleteAttachmentSuccessEvent(event.attachmentId, event.source, event.reference));
      }),
    )
    .catch(() => {
      dispatch([showError({ key: "dbot.attachment.attachmentDeleteError" })]);
    })
    .finally(() => {
      dispatch({ type: "dbot/change-state", state: { type: "ready" } });
    });
};

export const downloadAttachment = ({
  projectId,
  attachmentId,
  http,
}: {
  projectId: string;
  attachmentId: string;
  http: AxiosInstance;
}): Promise<{ data: Blob; filename: string | undefined }> =>
  http
    .post(`/project/${projectId}/attachment/${attachmentId}/download`, null, {
      responseType: "blob",
    })
    .then((res) => ({ data: res.data, filename: getFilenameFromHeaders(res.headers) }));

export const prepDocumentAttachmentsPanel = (
  context: DocumentContext<DocumentData>,
  dispatch: DbotEventDispatcher,
): AttachmentPanelProps | undefined =>
  (context.isLocked && !context.attachments.length) || !supportsAttachments(context)
    ? undefined
    : {
        title: false,
        uploadedAttachments: context.attachments,
        fileUploadConfig: context.fileUploadConfig,
        isDeleting: (id) =>
          context.state.type === "deleting-attachment" && context.state.attachmentId === id,
        onOpen: (attachmentId) => dispatch({ type: "dbot/open-attachment", attachmentId }),
        onDelete: context.isLocked
          ? undefined
          : (attachmentId) =>
              dispatch({
                type: "dbot/delete-attachment",
                attachmentId,
                reference: context.documentId,
                source: context.documentId,
              }),
      };

export const prepUploadDocumentAttachmentsButton = (
  context: DocumentContext<DocumentData>,
  dispatch: DbotEventDispatcher,
): SelectFilesButtonProps | undefined =>
  supportsAttachments(context) && !context.isLocked
    ? {
        variant: "link",
        icon: "upload",
        label: { key: "upload-attachments" },
        fileUploadConfig: context.fileUploadConfig,
        isDisabled: context.isLocked,
        isLoading:
          context.state.type === "uploading-attachments" &&
          context.state.reference === context.documentId,
        onChange: (files) =>
          dispatch({
            type: "dbot/upload-attachments",
            attachmentType: AttachmentType.UPLOADED_FILE,
            files,
            reference: context.documentId,
            source: context.documentId,
          }),
      }
    : undefined;

export const prepDecisionAttachmentsPanel = (
  context: ProjectContext,
  decisionId: string,
  attachments: ProjectDecisionAttachment[],
  dispatch: DbotEventDispatcher,
): DecisionAttachmentPanelProps | undefined =>
  supportsAttachments(context)
    ? {
        title: true,
        uploadedAttachments: attachments,
        currentDocumentTypes: sortDocuments(context.currentDocuments)
          .filter(
            (d) =>
              d.type !== DocumentType.custom_document &&
              d.type !== DocumentType.articlesofassociation,
          )
          .map((d) => d.type),
        fileUploadConfig: context.fileUploadConfig,
        isDeleting: (id) =>
          context.state.type === "deleting-attachment" && context.state.attachmentId === id,
        onOpen: (attachmentId) => dispatch({ type: "dbot/open-attachment", attachmentId }),
        onDelete: context.isLocked
          ? undefined
          : (attachmentId) =>
              dispatch({
                type: "dbot/delete-attachment",
                attachmentId,
                reference: decisionId,
                source: decisionId,
              }),
        onToggleDocumentType: context.isLocked
          ? undefined
          : (attachment, documentType) => {
              runInAction(() => {
                const index = attachment.selectedDocuments.findIndex((d) => d === documentType);
                if (index >= 0) {
                  attachment.selectedDocuments.splice(index, 1);
                } else {
                  attachment.selectedDocuments.push(documentType);
                }
              });
            },
        isLocked: context.isLocked,
      }
    : undefined;

export const prepUploadDecisionAttachmentsButton = (
  context: ProjectContext,
  decisionId: string,
  dispatch: DbotEventDispatcher,
): SelectFilesButtonProps | undefined =>
  supportsAttachments(context)
    ? {
        variant: "secondary",
        icon: "upload",
        label: { key: "upload-attachments" },
        fileUploadConfig: context.fileUploadConfig,
        isDisabled: context.isLocked,
        isLoading:
          context.state.type === "uploading-attachments" && context.state.reference === decisionId,
        onChange: (files) =>
          dispatch({
            type: "dbot/upload-attachments",
            attachmentType: AttachmentType.UPLOADED_FILE,
            files,
            reference: decisionId,
            source: decisionId,
          }),
      }
    : undefined;
